import { StrictMode, useEffect, useState, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import type { OrgDescriptor } from '@wand/shared';
import { router } from './router.js';
import { api } from './api/client.js';
import { DEFAULT_ORGS, OrgProvider } from './org/OrgContext.js';
import { UserProvider } from './org/UserContext.js';
import { GatewayProvider } from './gateway/GatewayContext.js';
import { Splash } from './components/Splash.js';
import { DEMO_ENABLED } from './demo/flag.js';
import { initTheme } from './theme.js';
import './styles.css';

// Reflect the persisted theme onto <html> (the index.html bootstrap does this pre-paint;
// this keeps the in-memory store and the attribute in sync for the app's lifetime).
initTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

/**
 * Fetches the org registry from the BFF before mounting the org context.
 * Demo mode offers every org; production offers only real (non-demo) tenants.
 *
 * The fetch RETRIES with backoff before giving up, so a transient BFF blip
 * (e.g. a restart mid-session) can't silently strand the app on the single-org
 * `DEFAULT_ORGS` fallback — the demo orgs (Acme, Atlas Health, …) come back on
 * their own once the BFF answers. Falling back to the built-in default only
 * happens if the BFF stays unreachable across every attempt.
 */
async function listOrgsWithRetry(attempts = 6): Promise<OrgDescriptor[]> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await api.listOrgs();
    } catch (e) {
      lastErr = e;
      await new Promise((r) => setTimeout(r, Math.min(2000, 300 * (i + 1))));
    }
  }
  throw lastErr;
}

function OrgRegistryGate({ children }: { children: ReactNode }) {
  const [orgs, setOrgs] = useState<OrgDescriptor[] | null>(null);

  useEffect(() => {
    let alive = true;
    listOrgsWithRetry()
      .then((all) => {
        const offered = DEMO_ENABLED ? all : all.filter((o) => !o.isDemo);
        if (alive) setOrgs(offered.length > 0 ? offered : DEFAULT_ORGS);
      })
      .catch(() => {
        if (alive) setOrgs(DEFAULT_ORGS);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!orgs) return <Splash label="Loading workspace…" />;
  return <OrgProvider orgs={orgs}>{children}</OrgProvider>;
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <OrgRegistryGate>
        <GatewayProvider>
          <UserProvider>
            <RouterProvider router={router} />
          </UserProvider>
        </GatewayProvider>
      </OrgRegistryGate>
    </QueryClientProvider>
  </StrictMode>,
);
